import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Search, X, ScanLine, AlertTriangle, ChevronRight,
  Home, FileText, CreditCard,
  LineChart, UserCircle2, ChevronDown, Plus, Users, Map, Calendar, CheckCircle2, Loader2,
  Banknote, CheckCircle, Clock, ArrowLeftRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ScanReceiptModal from '../components/ScanReceiptModal';
import BuddyLogo from '../components/BuddyLogo';
import ReceiptStatusBadge, { getReceiptTapAction } from '../components/ReceiptStatusBadge';
import CouldntReadModal from '../components/CouldntReadModal';
import {
  formatReceiptDate,
  getMerchantAvatar,
  getCategoryPill,
  getGroupTypeConfig,
  getMemberAvatarColor,
  getMemberInitials,
} from '../utils/formatters';

// ── Currency formatter ───────────────────────────────────────────────────────
const CURRENCY_SYMBOLS = { GBP: '£', INR: '₹', USD: '$', EUR: '€', AED: 'د.إ', AUD: 'A$' };
const formatCurrency = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[(currency || '').toUpperCase()] || `${currency || ''} `;
  return `${symbol}${(amount || 0).toFixed(2)}`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GRADIENT_HEADER = 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)';
const PRIMARY = '#2D5BFF';

const NAV_ITEMS = [
  { icon: Home, label: 'Home', key: 'home' },
  { icon: FileText, label: 'Receipts', key: 'receipts' },
  { icon: CreditCard, label: 'Cards', key: 'cards' },
  { icon: LineChart, label: 'Insights', key: 'insights' },
  { icon: UserCircle2, label: 'Profile', key: 'profile' },
];

const GroupSwitcherPill = ({ selectedGroup, avatarUrl, userName, onClick }) => {
  if (selectedGroup) {
    const cfg = getGroupTypeConfig(selectedGroup.type);
    const Icon = cfg.icon;
    return (
      <button onClick={onClick} className="flex items-center gap-2" style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '6px 14px 6px 8px', flexShrink: 0 }}>
        <div className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 15 }}>
          {selectedGroup.emoji
            ? <span style={{ lineHeight: 1 }}>{selectedGroup.emoji}</span>
            : <Icon className="w-3.5 h-3.5" style={{ color: '#FFFFFF' }} />}
        </div>
        <span className="truncate" style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', maxWidth: 100 }}>{selectedGroup.name}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
      </button>
    );
  }
  const initial = (userName || 'U').charAt(0).toUpperCase();
  return (
    <button onClick={onClick} className="flex items-center gap-2" style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '6px 14px 6px 8px', flexShrink: 0 }}>
      <div className="flex items-center justify-center overflow-hidden" style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: avatarUrl ? 'transparent' : 'rgba(255,255,255,0.2)', color: '#FFFFFF', fontWeight: 700, fontSize: 11, fontFamily: 'Manrope, sans-serif' }}>
        {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : initial}
      </div>
      <span style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>Personal</span>
      <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
    </button>
  );
};

const GroupSwitcherSheet = ({ isOpen, onClose, groups, selectedGroupId, onSelect, onCreateGroup, onManageGroups, userName, avatarUrl }) => {
  if (!isOpen) return null;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} />
      <div className="fixed left-0 right-0 bottom-0 z-50 flex flex-col" style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '72vh', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
        {/* Fixed header */}
        <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
          <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>
          <p style={{ color: '#6B7280', fontSize: 12, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', letterSpacing: 1, marginBottom: 4 }}>SWITCH TO</p>
        </div>
        {/* Scrollable list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 20px' }}>
          <button onClick={() => onSelect(null)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? 'transparent' : PRIMARY, color: '#FFFFFF', fontWeight: 700, fontSize: 14, fontFamily: 'Manrope, sans-serif' }}>
              {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (userName || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Personal</p>
              <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>Just your receipts, cards &amp; vouchers</p>
            </div>
            {!selectedGroupId && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
          </button>
          {groups.map((g) => {
            const cfg = getGroupTypeConfig(g.type);
            const Icon = cfg.icon;
            return (
              <button key={g.id} onClick={() => onSelect(g)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 20 }}>
                  {g.emoji
                    ? <span style={{ lineHeight: 1 }}>{g.emoji}</span>
                    : <Icon className="w-5 h-5" style={{ color: '#FFFFFF' }} />}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>{g.name}</p>
                  <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>{cfg.label} · {g.member_count} member{g.member_count !== 1 ? 's' : ''}</p>
                </div>
                {selectedGroupId === g.id && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
              </button>
            );
          })}
        </div>
        {/* Fixed footer */}
        <div style={{ borderTop: '1px solid #F3F4F6', padding: '4px 20px 20px', flexShrink: 0 }}>
          <button onClick={onCreateGroup} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: '#F3F4F6' }}><Plus className="w-5 h-5" style={{ color: PRIMARY }} /></div>
            <p style={{ color: PRIMARY, fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Create a circle</p>
          </button>
          <button onClick={onManageGroups} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: '#F3F4F6' }}><Users className="w-5 h-5" style={{ color: '#6B7280' }} /></div>
            <p style={{ color: '#6B7280', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Manage circles</p>
          </button>
        </div>
      </div>
    </>
  );
};

// ── Merchant Avatar ─────────────────────────────────────────────────────────
const MerchantAvatar = ({ name, size = 44 }) => {
  const { initials, bgColor, textColor } = getMerchantAvatar(name);
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: 12,
        backgroundColor: bgColor, color: textColor,
        fontWeight: 700, fontSize: size * 0.32,
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {initials}
    </div>
  );
};

// ── Category Badge ──────────────────────────────────────────────────────────
const CategoryBadge = ({ category }) => {
  const pill = getCategoryPill(category);
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: pill.bg, color: pill.color }}
    >
      {category || 'Other'}
    </span>
  );
};


// ── Share Receipt Sheet ─────────────────────────────────────────────────────
// Step 1: checkbox group picker. Step 2: "Who paid?" (only for reimb-enabled circles).
const ShareReceiptSheet = ({ isOpen, receipt, groups, onClose, onShared }) => {
  const { user: authUser } = useAuth();

  // Step 1 state
  const [sharingInfo, setSharingInfo] = useState([]);
  const [checked, setChecked] = useState({});
  const [loadingSharing, setLoadingSharing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Step 2 state
  const [step, setStep] = useState('groups'); // 'groups' | 'whoPaid'
  const [pendingAdd, setPendingAdd] = useState([]);
  const [pendingRemove, setPendingRemove] = useState([]);
  const [whoPaidMembers, setWhoPaidMembers] = useState([]);
  const [whoPaidLabels, setWhoPaidLabels] = useState([]);
  const [paidByType, setPaidByType] = useState('member');
  const [paidByUserId, setPaidByUserId] = useState(null);
  const [paidByName, setPaidByName] = useState('');
  const [paidByLabelId, setPaidByLabelId] = useState(null);
  const [paidByLabelName, setPaidByLabelName] = useState(null);
  // Unified reimb target: { type: 'none'|'member'|'label', id, name }
  const [reimbTarget, setReimbTarget] = useState({ type: 'none', id: null, name: null });
  // Picker visibility + search
  const [showPaidByPicker, setShowPaidByPicker] = useState(false);
  const [showReimbPicker, setShowReimbPicker] = useState(false);
  const [paidBySearch, setPaidBySearch] = useState('');
  const [reimbSearch, setReimbSearch] = useState('');
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelType, setNewLabelType] = useState('bank');
  const [savingLabel, setSavingLabel] = useState(false);

  useEffect(() => {
    if (!isOpen || !receipt?.id) return;
    setStep('groups');
    setLoadingSharing(true);
    setSharingInfo([]);
    setChecked({});
    axios.get(`${API}/my-shared-items?item_id=${receipt.id}`, { withCredentials: true })
      .then(res => {
        const shared = res.data.shared_items || [];
        setSharingInfo(shared);
        const map = {};
        groups.forEach(g => { map[g.id] = false; });
        shared.forEach(s => { map[s.group_id] = true; });
        setChecked(map);
      })
      .catch(() => {})
      .finally(() => setLoadingSharing(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, receipt?.id]);

  const handleToggle = (groupId) => {
    setChecked(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleUpdate = async () => {
    const existingMap = {};
    sharingInfo.forEach(s => { existingMap[s.group_id] = s.share_id; });
    const toAdd    = groups.filter(g =>  checked[g.id] && !existingMap[g.id]);
    const toRemove = sharingInfo.filter(s => !checked[s.group_id]);

    // Check if any circles being added have reimbursement tracking
    const reimbCircles = toAdd.filter(g => g.reimbursement_tracking_enabled);

    if (reimbCircles.length > 0) {
      // Fetch members and labels for the first reimbursement circle
      const firstReimb = reimbCircles[0];
      const myName = `${authUser?.first_name || ''} ${authUser?.last_name || ''}`.trim();
      let members = [];
      let labels = [];

      try {
        const summaryRes = await axios.get(`${API}/groups/${firstReimb.id}/summary`, { withCredentials: true });
        members = (summaryRes.data.members || []).map(m => ({
          user_id: m.user_id || m.email,
          name: m.display_name || m.email,
          avatar_url: m.avatar_url,
        }));
      } catch { /* show empty list */ }

      try {
        const labelsRes = await axios.get(`${API}/groups/${firstReimb.id}/labels`, { withCredentials: true });
        labels = labelsRes.data.labels || [];
      } catch { /* show no labels */ }

      // Set defaults
      setPaidByType('member');
      setPaidByUserId(authUser?.id || null);
      setPaidByName(myName);
      // Reimbursed by: group default → first label → none
      const grp = firstReimb;
      let defaultReimb = { type: 'none', id: null, name: null };
      if (grp.default_reimb_label_name) {
        const lbl = labels.find(l => l.name === grp.default_reimb_label_name) || labels[0];
        if (lbl) defaultReimb = { type: 'label', id: lbl.id, name: lbl.name };
      } else if (grp.default_reimb_user_id) {
        defaultReimb = { type: 'member', id: grp.default_reimb_user_id, name: grp.default_reimb_name || '' };
      } else if (labels.length > 0) {
        defaultReimb = { type: 'label', id: labels[0].id, name: labels[0].name };
      }
      setReimbTarget(defaultReimb);
      setShowPaidByPicker(false);
      setShowReimbPicker(false);
      setPaidBySearch('');
      setReimbSearch('');
      setWhoPaidMembers(members);
      setWhoPaidLabels(labels);
      setPendingAdd(toAdd);
      setPendingRemove(toRemove);
      setStep('whoPaid');
    } else {
      // No reimbursement circles — share directly
      await executeShare(toAdd, toRemove, null);
    }
  };

  const executeShare = async (toAdd, toRemove, reimbPayload) => {
    setSaving(true);
    try {
      const existingMap = {};
      sharingInfo.forEach(s => { existingMap[s.group_id] = s.share_id; });
      await Promise.all([
        ...toAdd.map(g => {
          const body = { item_id: receipt.id, item_type: 'receipt' };
          // Add reimbursement fields for circles that have tracking enabled
          if (reimbPayload && g.reimbursement_tracking_enabled) {
            Object.assign(body, reimbPayload);
          }
          return axios.post(`${API}/groups/${g.id}/share`, body, { withCredentials: true });
        }),
        ...toRemove.map(s =>
          axios.delete(`${API}/groups/${s.group_id}/share/${s.share_id}`, { withCredentials: true })
        ),
      ]);
      const parts = [];
      if (toAdd.length)    parts.push(`Shared with ${toAdd.map(g => g.name).join(', ')}`);
      if (toRemove.length) parts.push(`Removed from ${toRemove.map(s => s.group_name).join(', ')}`);
      if (parts.length) toast.success(parts.join(' · '));
      else toast.info('No changes made');
      onShared?.();
      onClose();
    } catch {
      toast.error("Couldn't update sharing");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmShare = async () => {
    const isLabelDirect = paidByType === 'label'; // label paid directly — no reimb
    const reimbPayload = {
      paid_by_type:         isLabelDirect ? 'label' : 'member',
      paid_by_user_id:      isLabelDirect ? null : (paidByUserId || authUser?.id || null),
      paid_by_name:         isLabelDirect ? null : (paidByName || null),
      paid_by_label_id:     isLabelDirect ? paidByLabelId : (reimbTarget.type !== 'none' ? reimbTarget.id : null),
      paid_by_label_name:   isLabelDirect ? paidByLabelName : (reimbTarget.type !== 'none' ? reimbTarget.name : null),
      reimbursement_status: isLabelDirect || reimbTarget.type === 'none' ? 'not_applicable' : 'pending',
    };
    await executeShare(pendingAdd, pendingRemove, reimbPayload);
  };

  const handleAddLabel = async () => {
    const reimbCircle = pendingAdd.find(g => g.reimbursement_tracking_enabled);
    if (!newLabelName.trim() || !reimbCircle) return;
    setSavingLabel(true);
    try {
      const res = await axios.post(`${API}/groups/${reimbCircle.id}/labels`, { name: newLabelName.trim(), label_type: newLabelType }, { withCredentials: true });
      const label = res.data.label;
      setWhoPaidLabels(prev => [...prev, label]);
      setNewLabelName('');
      setShowAddLabel(false);
      toast.success('Label added');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add label");
    } finally { setSavingLabel(false); }
  };

  if (!isOpen || !receipt) return null;

  // ── Step 2: Who paid? — clean 2-row design ──────────────────────────────
  if (step === 'whoPaid') {
    const myName = `${authUser?.first_name || ''} ${authUser?.last_name || ''}`.trim();
    const isLabelPayer = paidByType === 'label';
    const displayName = isLabelPayer ? reimbTarget.name : (paidByName || myName);
    const reimbDisplay = reimbTarget.type === 'none' ? 'Not getting reimbursed' : reimbTarget.name;
    const isReimbLabel  = reimbTarget.type === 'label';
    const isReimbMember = reimbTarget.type === 'member';

    return (
      <>
        <div onClick={() => { setStep('groups'); setShowPaidByPicker(false); setShowReimbPicker(false); }} className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 60 }} />
        <div className="fixed left-0 right-0 bottom-0 flex flex-col" style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, zIndex: 61, maxHeight: '90vh', overflowY: 'auto', padding: '0 20px 40px', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
          {/* Handle */}
          <div className="flex justify-center pt-3 mb-3"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)', borderRadius: 14, padding: '14px 16px', marginBottom: 24 }}>
            <p style={{ color: '#fff', fontSize: 17, fontWeight: 700, fontFamily: 'Manrope, sans-serif', marginBottom: 2 }}>Share to {pendingAdd.map(g => g.name).join(', ')}</p>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>{receipt.merchant} · {formatCurrency(receipt.amount, receipt.currency)}</p>
          </div>

          {/* ── Paid by row ─────────────────────────────────────────────────── */}
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', marginBottom: 8, fontFamily: 'DM Sans, sans-serif' }}>Paid by</p>
            <button
              onClick={() => { setShowPaidByPicker(v => !v); setShowReimbPicker(false); setPaidBySearch(''); }}
              className="w-full flex items-center gap-3"
              style={{ backgroundColor: '#F8FAFF', border: `1.5px solid ${showPaidByPicker ? '#2D5BFF' : '#E5E7EB'}`, borderRadius: showPaidByPicker ? '12px 12px 0 0' : 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: isLabelPayer ? '#0D9488' : '#2D5BFF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {isLabelPayer ? <Banknote className="w-4 h-4" style={{ color: '#fff' }} /> : displayName.charAt(0).toUpperCase()}
              </div>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>
                {displayName}
                {!isLabelPayer && (paidByUserId === authUser?.id || !paidByUserId) && <span style={{ color: '#9CA3AF', fontSize: 12, marginLeft: 4 }}>(You)</span>}
              </span>
              <ChevronRight className="w-4 h-4" style={{ color: '#9CA3AF', transform: showPaidByPicker ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
            </button>

            {showPaidByPicker && (
              <div style={{ border: '1.5px solid #2D5BFF', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#F8FAFF' }}>
                  <input type="text" placeholder="Search members..." value={paidBySearch} onChange={e => setPaidBySearch(e.target.value)}
                    style={{ width: '100%', fontSize: 16, fontFamily: 'DM Sans, sans-serif', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px', outline: 'none' }} />
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {/* Members */}
                  {[{ user_id: authUser?.id, name: myName }, ...whoPaidMembers.filter(m => m.user_id !== authUser?.id && m.user_id !== authUser?.email)]
                    .filter(m => !paidBySearch || m.name.toLowerCase().includes(paidBySearch.toLowerCase()))
                    .map(m => {
                      const sel = paidByType !== 'label' && (paidByUserId === m.user_id || (!paidByUserId && m.user_id === authUser?.id));
                      return (
                        <button key={m.user_id} onClick={() => { setPaidByType('member'); setPaidByUserId(m.user_id); setPaidByName(m.name); setShowPaidByPicker(false); setPaidBySearch(''); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5"
                          style={{ backgroundColor: sel ? '#EFF6FF' : 'transparent', borderBottom: '1px solid #F9FAFB', cursor: 'pointer', textAlign: 'left' }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? '#2D5BFF' : '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{m.name.charAt(0)}</div>
                          <span style={{ flex: 1, fontSize: 14, color: '#111827', fontFamily: 'DM Sans, sans-serif' }}>
                            {m.name}{m.user_id === authUser?.id && <span style={{ color: '#9CA3AF', fontSize: 12, marginLeft: 4 }}>(You)</span>}
                          </span>
                          {sel && <CheckCircle className="w-4 h-4" style={{ color: '#2D5BFF', flexShrink: 0 }} />}
                        </button>
                      );
                    })}
                  {/* Labels — direct payment, no reimbursement */}
                  {whoPaidLabels.filter(l => !paidBySearch || l.name.toLowerCase().includes(paidBySearch.toLowerCase())).length > 0 && (<>
                    <div style={{ padding: '5px 12px', fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#F9FAFB', fontFamily: 'DM Sans, sans-serif' }}>Direct payment (no reimbursement)</div>
                    {whoPaidLabels.filter(l => !paidBySearch || l.name.toLowerCase().includes(paidBySearch.toLowerCase())).map(lbl => {
                      const sel = paidByType === 'label' && paidByLabelId === lbl.id;
                      return (
                        <button key={lbl.id} onClick={() => { setPaidByType('label'); setPaidByUserId(null); setPaidByName(null); setPaidByLabelId(lbl.id); setPaidByLabelName(lbl.name); setReimbTarget({ type: 'none', id: null, name: null }); setShowPaidByPicker(false); setPaidBySearch(''); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5"
                          style={{ backgroundColor: sel ? '#F0FDFA' : 'transparent', borderBottom: '1px solid #F9FAFB', cursor: 'pointer', textAlign: 'left' }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? '#0D9488' : '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Banknote className="w-3.5 h-3.5" style={{ color: sel ? '#fff' : '#0D9488' }} /></div>
                          <span style={{ flex: 1, fontSize: 14, color: '#111827', fontFamily: 'DM Sans, sans-serif' }}>{lbl.name}</span>
                          {sel && <CheckCircle className="w-4 h-4" style={{ color: '#0D9488', flexShrink: 0 }} />}
                        </button>
                      );
                    })}
                  </>)}
                </div>
              </div>
            )}
          </div>

          {/* ── Reimbursed by — always shown, defaults to None ────────────────── */}
          <div style={{ marginBottom: 28 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', marginBottom: 8, fontFamily: 'DM Sans, sans-serif' }}>Reimbursed by</p>
            <button
              onClick={() => { setShowReimbPicker(v => !v); setShowPaidByPicker(false); setReimbSearch(''); }}
              className="w-full flex items-center gap-3"
              style={{ backgroundColor: reimbTarget.type === 'none' ? '#F9FAFB' : '#F0FDFA', border: `1.5px solid ${showReimbPicker ? '#0D9488' : reimbTarget.type === 'none' ? '#E5E7EB' : '#99F6E4'}`, borderRadius: showReimbPicker ? '12px 12px 0 0' : 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: reimbTarget.type === 'none' ? '#F3F4F6' : isReimbLabel ? '#0D9488' : '#2D5BFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {reimbTarget.type === 'none' ? <span style={{ color: '#9CA3AF', fontSize: 18 }}>–</span>
                 : isReimbLabel ? <Banknote className="w-4 h-4" style={{ color: '#fff' }} />
                 : <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>{(reimbTarget.name || '?').charAt(0)}</span>}
              </div>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: reimbTarget.type === 'none' ? '#9CA3AF' : '#111827', fontFamily: 'Manrope, sans-serif' }}>{reimbDisplay}</span>
              <ChevronRight className="w-4 h-4" style={{ color: '#9CA3AF', transform: showReimbPicker ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
            </button>

            {showReimbPicker && (
              <div style={{ border: '1.5px solid #0D9488', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#F8FFFE' }}>
                  <input type="text" placeholder="Search..." value={reimbSearch} onChange={e => setReimbSearch(e.target.value)}
                    style={{ width: '100%', fontSize: 16, fontFamily: 'DM Sans, sans-serif', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px', outline: 'none' }} />
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {/* None */}
                  {!reimbSearch && (
                    <button onClick={() => { setReimbTarget({ type: 'none', id: null, name: null }); setShowReimbPicker(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5"
                      style={{ backgroundColor: reimbTarget.type === 'none' ? '#F9FAFB' : 'transparent', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><span style={{ color: '#9CA3AF', fontSize: 16 }}>–</span></div>
                      <span style={{ flex: 1, fontSize: 14, color: '#6B7280', fontFamily: 'DM Sans, sans-serif' }}>Not getting reimbursed</span>
                      {reimbTarget.type === 'none' && <CheckCircle className="w-4 h-4" style={{ color: '#6B7280', flexShrink: 0 }} />}
                    </button>
                  )}
                  {/* Members */}
                  {(() => {
                    const filtered = whoPaidMembers.filter(m => {
                      if (m.user_id === paidByUserId) return false; // payer can't reimburse themselves
                      return !reimbSearch || m.name.toLowerCase().includes(reimbSearch.toLowerCase());
                    });
                    if (!filtered.length) return null;
                    return (<>
                      <div style={{ padding: '5px 12px', fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#F9FAFB', fontFamily: 'DM Sans, sans-serif' }}>Members</div>
                      {filtered.map(m => {
                        const sel = reimbTarget.type === 'member' && reimbTarget.id === m.user_id;
                        return (
                          <button key={m.user_id} onClick={() => { setReimbTarget({ type: 'member', id: m.user_id, name: m.name }); setShowReimbPicker(false); setReimbSearch(''); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5"
                            style={{ backgroundColor: sel ? '#EFF6FF' : 'transparent', borderBottom: '1px solid #F9FAFB', cursor: 'pointer' }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? '#2D5BFF' : '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{m.name.charAt(0)}</div>
                            <span style={{ flex: 1, fontSize: 14, color: '#111827', fontFamily: 'DM Sans, sans-serif' }}>{m.name}</span>
                            {sel && <CheckCircle className="w-4 h-4" style={{ color: '#2D5BFF', flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </>);
                  })()}
                  {/* Labels */}
                  {(() => {
                    const filtered = whoPaidLabels.filter(l => {
                      if (isLabelPayer && l.id === paidByLabelId) return false; // paid-by label can't reimburse itself
                      return !reimbSearch || l.name.toLowerCase().includes(reimbSearch.toLowerCase());
                    });
                    if (!filtered.length) return null;
                    return (<>
                      <div style={{ padding: '5px 12px', fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#F9FAFB', fontFamily: 'DM Sans, sans-serif' }}>Payment labels</div>
                      {filtered.map(lbl => {
                        const sel = reimbTarget.type === 'label' && reimbTarget.id === lbl.id;
                        return (
                          <button key={lbl.id} onClick={() => { setReimbTarget({ type: 'label', id: lbl.id, name: lbl.name }); setShowReimbPicker(false); setReimbSearch(''); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5"
                            style={{ backgroundColor: sel ? '#F0FDFA' : 'transparent', borderBottom: '1px solid #F9FAFB', cursor: 'pointer' }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? '#0D9488' : '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Banknote className="w-3.5 h-3.5" style={{ color: sel ? '#fff' : '#0D9488' }} /></div>
                            <span style={{ flex: 1, fontSize: 14, color: '#111827', fontFamily: 'DM Sans, sans-serif' }}>{lbl.name}</span>
                            {sel && <CheckCircle className="w-4 h-4" style={{ color: '#0D9488', flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </>);
                  })()}
                </div>
              </div>
            )}
          </div>

          <button onClick={handleConfirmShare} disabled={saving} className="w-full flex items-center justify-center gap-2"
            style={{ background: saving ? '#93C5FD' : 'linear-gradient(135deg, #2D5BFF, #1B3DBF)', borderRadius: 16, padding: 14, color: '#fff', fontSize: 15, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', border: 'none', cursor: 'pointer' }}>
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {saving ? 'Sharing…' : 'Confirm & share →'}
          </button>
          <button onClick={() => { setStep('groups'); setShowPaidByPicker(false); setShowReimbPicker(false); }}
            style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 13, fontWeight: 600, padding: '10px', cursor: 'pointer', width: '100%', marginTop: 4 }}>← Back</button>
        </div>
      </>
    );
  }


  // ── Step 1: Group selection ───────────────────────────────────────────────
  return (
    <>
      <div onClick={onClose} className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 60 }} />
      <div
        className="fixed left-0 right-0 bottom-0 flex flex-col"
        style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '72vh', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)', zIndex: 61 }}
      >
        {/* Fixed header */}
        <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
          <div className="flex justify-center mb-3">
            <div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} />
          </div>
          <div className="flex items-start justify-between mb-1">
            <div>
              <p style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Share with circles</p>
              <p style={{ color: '#9CA3AF', fontSize: 12, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                {receipt.merchant} · {formatCurrency(receipt.amount, receipt.currency)}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: 'none', cursor: 'pointer' }}
            >
              <X className="w-4 h-4" style={{ color: '#6B7280' }} />
            </button>
          </div>
          <div style={{ height: 1, backgroundColor: '#F3F4F6', marginTop: 10 }} />
        </div>

        {/* Scrollable group list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 20px' }}>
          {loadingSharing ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: PRIMARY }} />
            </div>
          ) : groups.length === 0 ? (
            <div style={{ padding: '28px 8px', textAlign: 'center' }}>
              <p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
                You're not part of any circle yet. Create one from your profile and invite your loved ones — sharing is that easy.
              </p>
            </div>
          ) : (
            groups.map((g) => {
              const cfg = getGroupTypeConfig(g.type);
              const Icon = cfg.icon;
              const isChecked = !!checked[g.id];
              return (
                <button
                  key={g.id}
                  onClick={() => handleToggle(g.id)}
                  className="w-full flex items-center gap-3 py-3"
                  style={{ borderBottom: '1px solid #F3F4F6' }}
                >
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 20 }}>
                    {g.emoji ? <span style={{ lineHeight: 1 }}>{g.emoji}</span> : <Icon className="w-5 h-5" style={{ color: '#FFFFFF' }} />}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>{g.name}</p>
                    <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>
                      {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                      {g.reimbursement_tracking_enabled && (
                        <ArrowLeftRight className="w-3 h-3" style={{ marginLeft: 6, color: '#0D9488', flexShrink: 0 }} />
                      )}
                    </p>
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    border: isChecked ? `2px solid ${PRIMARY}` : '2px solid #D1D5DB',
                    backgroundColor: isChecked ? PRIMARY : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s',
                  }}>
                    {isChecked && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Fixed footer */}
        {groups.length > 0 && (
          <div style={{ padding: '12px 20px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
            <button
              onClick={handleUpdate}
              disabled={saving || loadingSharing}
              className="w-full flex items-center justify-center gap-2"
              style={{
                height: 48, borderRadius: 14, border: 'none',
                background: GRADIENT_HEADER, color: '#FFFFFF',
                fontSize: 15, fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
                cursor: (saving || loadingSharing) ? 'not-allowed' : 'pointer',
                opacity: (saving || loadingSharing) ? 0.7 : 1,
              }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update sharing'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ── Receipt Row ─────────────────────────────────────────────────────
// onShare: optional fn(receipt) — when provided shows share icon (Personal view only)
const ReceiptRow = ({ receipt, onClick, onShare }) => {
  const itemCount = receipt?.items?.length || 0;
  const isPending = receipt.scan_status === 'pending_retry' || receipt.scan_status === 'needs_review';
  const isFailed = receipt.scan_status === 'failed';
  const canShare = !!onShare && !isPending && !isFailed;

  // Extract first name only from shared_by
  const sharedByFirst = receipt._shared_by
    ? receipt._shared_by.split(' ')[0]
    : null;

  return (
    <div
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onClick(receipt)}
      style={receipt._reimbursement_status === 'pending' ? { backgroundColor: '#FFFBEB' } : {}}
    >
      <MerchantAvatar name={receipt.merchant} size={44} />
      <div className="flex-1 min-w-0">
        {/* Row 1: merchant name + amount */}
        <div className="flex items-center justify-between gap-2">
          <span
            className="font-semibold text-gray-900 truncate text-sm"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {isPending ? 'Processing…' : isFailed ? (receipt.merchant && receipt.merchant !== 'Processing…' ? receipt.merchant : 'Unknown') : receipt.merchant}
          </span>
          {!isPending && !isFailed && (
            <span className="font-bold text-gray-900 text-sm flex-shrink-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(receipt.amount, receipt.currency)}
            </span>
          )}
          {isFailed && <ReceiptStatusBadge status={receipt.scan_status} size="xs" compact={false} />}
        </div>
        {/* Row 2: meta + share link */}
        <div className="flex items-center justify-between gap-2 mt-1">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {isPending ? (
              <ReceiptStatusBadge status={receipt.scan_status} size="xs" />
            ) : (
              <>
                <ReceiptStatusBadge status={receipt.scan_status} compact={true} />
                <CategoryBadge category={receipt.category} />
                {itemCount > 0 && (
                  <span className="text-xs text-gray-400">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                )}
                <span className="text-xs text-gray-400">·</span>
                <span className="text-xs text-gray-400">{formatReceiptDate(receipt.transaction_date)}</span>
              </>
            )}
          </div>
          {/* Share link — Personal view only */}
          {canShare && (
            <button
              onClick={(e) => { e.stopPropagation(); onShare(receipt); }}
              className="flex items-center gap-1 flex-shrink-0"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
            >
              <Users className="w-3.5 h-3.5 flex-shrink-0" style={{ color: PRIMARY }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: PRIMARY, fontFamily: 'DM Sans, sans-serif' }}>Share</span>
            </button>
          )}
        </div>
        {/* Row 3: shared by + reimbursement badge — always own line */}
        {sharedByFirst && (
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="text-xs text-gray-400">· by {sharedByFirst}</span>
            {receipt._reimbursement_status === 'pending' && (
              <span
                className="flex items-center gap-0.5"
                style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 4, padding: '0px 5px', fontSize: 9, fontWeight: 700, color: '#92400E', fontFamily: 'DM Sans, sans-serif', lineHeight: '16px' }}
              >
                ⏳ Pending
              </span>
            )}
            {receipt._reimbursement_status === 'received' && (
              <span
                style={{ backgroundColor: '#CCFBF1', border: '1px solid #99F6E4', borderRadius: 4, padding: '0px 5px', fontSize: 9, fontWeight: 700, color: '#065F46', fontFamily: 'DM Sans, sans-serif', lineHeight: '16px' }}
              >
                ✓ Received
              </span>
            )}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
    </div>
  );
};

// ── Month Section ───────────────────────────────────────────────────────────
const MonthSection = ({ label, receipts, onView, onShare }) => {
  // Only count completed receipts in the month total (consistent with Dashboard)
  const completed = receipts.filter((r) => r.scan_status === 'completed');
  const currencies = [...new Set(completed.map((r) => (r.currency || '').toUpperCase()).filter(Boolean))];
  const isMixed = currencies.length > 1;
  const total = completed.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between px-1 mb-2">
        <span
          className="text-xs font-bold text-gray-500 uppercase tracking-widest"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {label}
        </span>
        {!isMixed && currencies[0] && (
          <span className="text-xs font-bold text-gray-500">
            {formatCurrency(total, currencies[0])}
          </span>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        {receipts.map((receipt, idx) => (
          <div key={receipt.id}>
            <ReceiptRow receipt={receipt} onClick={onView} onShare={onShare} />
            {idx < receipts.length - 1 && <div className="h-px bg-gray-100 mx-4" />}
          </div>
        ))}
      </div>
    </div>
  );
};


// ── Month Filter Pill ───────────────────────────────────────────────────────
const MonthFilterPill = ({ month, isActive, onClick }) => (
  <button
    onClick={onClick}
    className="px-4 py-1.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap flex-shrink-0"
    style={{
      backgroundColor: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.18)',
      color: isActive ? PRIMARY : 'rgba(255,255,255,0.92)',
      fontFamily: 'Manrope, sans-serif',
      border: isActive ? 'none' : '1px solid rgba(255,255,255,0.22)',
    }}
  >
    {month.label}
  </button>
);

// ── Empty State ─────────────────────────────────────────────────────────────
const EmptyState = ({ searchQuery, selectedMonth, onScan }) => {
  const monthName = selectedMonth
    ? new Date(selectedMonth.startDate).toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    })
    : null;

  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div
        className="w-16 h-16 rounded-2xl mb-4 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(45,91,255,0.08)' }}
      >
        <FileText className="w-8 h-8" style={{ color: PRIMARY }} />
      </div>
      <div
        className="font-bold text-gray-900 text-lg mb-1"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {searchQuery
          ? 'No receipts found'
          : monthName
            ? `No receipts for ${monthName}`
            : 'No receipts yet'}
      </div>
      <div className="text-gray-400 text-sm mb-5">
        {searchQuery
          ? 'Try different search terms'
          : monthName
            ? `Nothing saved for ${monthName}`
            : 'Scan your first receipt to get started'}
      </div>
      {!searchQuery && !selectedMonth && (
        <button
          onClick={onScan}
          className="px-6 py-2.5 rounded-full font-bold text-white text-sm"
          style={{ background: GRADIENT_HEADER, fontFamily: 'Manrope, sans-serif' }}
        >
          Scan first receipt
        </button>
      )}
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────

// ── Animation helpers for Group Empty State ────────────────────────────
const ReceiptAnimRow = ({ name, initials, bg, amount, sub, isMobile }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 6,
    padding: isMobile ? '6px 5px' : '7px 6px',
    background: '#fff', borderRadius: isMobile ? 7 : 8,
    marginBottom: isMobile ? 3 : 4,
  }}>
    <div style={{
      width: isMobile ? 22 : 26, height: isMobile ? 22 : 26,
      borderRadius: 5, background: bg, color: '#fff',
      fontSize: isMobile ? 7 : 8, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Manrope, sans-serif', flexShrink: 0,
    }}>{initials}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#14213D', fontFamily: 'Manrope, sans-serif' }}>{name}</div>
      <div style={{ fontSize: isMobile ? 7 : 9, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>{sub}</div>
    </div>
    <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#14213D', fontFamily: 'Manrope, sans-serif', marginRight: 2 }}>{amount}</div>
    <div style={{
      width: isMobile ? 20 : 24, height: isMobile ? 20 : 24,
      borderRadius: 5, background: '#F0F2F5',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Users style={{ width: isMobile ? 10 : 12, height: isMobile ? 10 : 12, color: '#9CA3AF' }} />
    </div>
  </div>
);

const GroupAnimRow = ({ emoji, name, meta, bg, isMobile }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 6,
    padding: '5px 0', borderTop: '0.5px solid #F3F4F6',
  }}>
    <div style={{
      fontSize: isMobile ? 13 : 15, width: isMobile ? 22 : 26, height: isMobile ? 22 : 26,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 999, background: bg, flexShrink: 0,
    }}>{emoji}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 500, color: '#14213D', fontFamily: 'Manrope, sans-serif' }}>{name}</div>
      <div style={{ fontSize: isMobile ? 7 : 9, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>{meta}</div>
    </div>
    <div style={{
      width: isMobile ? 14 : 16, height: isMobile ? 14 : 16,
      borderRadius: '50%', border: '1.5px solid #D1D5DB', flexShrink: 0,
    }} />
  </div>
);

const GroupEmptyState = ({ groupName, onShareExisting, isMobile }) => {
  const [step, setStep] = useState(0);
  const frameRef = useRef(null);
  const shareBtnRef = useRef(null);
  const groupRowRef = useRef(null);
  const updateBtnRef = useRef(null);
  const [tapPos, setTapPos] = useState({ x: 0, y: 0 });

  // Animation step timings (ms)
  const TIMINGS = [1200, 500, 500, 500, 600, 400, 300, 400, 800, 800, 400];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setStep((prev) => (prev >= 10 ? 0 : prev + 1));
    }, TIMINGS[step]);
    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Position the tap indicator over the current target element
  useEffect(() => {
    if (!frameRef.current) return;
    const targetRef =
      step === 0 ? shareBtnRef :
      step === 3 ? groupRowRef :
      step === 5 ? updateBtnRef : null;
    if (targetRef?.current) {
      const fRect = frameRef.current.getBoundingClientRect();
      const tRect = targetRef.current.getBoundingClientRect();
      setTapPos({
        x: tRect.left - fRect.left + tRect.width / 2,
        y: tRect.top - fRect.top + tRect.height / 2,
      });
    }
  }, [step]);

  const showTap = step === 0 || step === 3 || step === 5;
  const shareHighlighted = step === 1;
  const rowRing = step === 1;
  const sheetOpen = step >= 2 && step <= 7;
  const overlayDim = step >= 2 && step <= 6;
  const checkChecked = step >= 4 && step <= 7;
  const btnActive = step >= 4 && step <= 7;
  const showSuccess = step === 8 || step === 9;

  const W = isMobile ? 240 : 320;
  const H = isMobile ? 310 : 340;

  return (
    <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
      {/* Animated device frame */}
      <div
        ref={frameRef}
        style={{
          width: W, height: H, borderRadius: 18,
          border: '1.5px solid #E5E7EB', overflow: 'hidden',
          position: 'relative', backgroundColor: '#F5F7FA',
          marginBottom: 20,
        }}
      >
        {/* Mini header */}
        <div style={{
          background: GRADIENT_HEADER,
          padding: isMobile ? '8px 10px 6px' : '10px 14px 8px',
          color: '#fff',
        }}>
          <div style={{
            fontSize: isMobile ? 11 : 13, fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
          }}>
            My Receipts
          </div>
        </div>

        {/* Receipt rows */}
        <div style={{ padding: isMobile ? '5px 8px' : '6px 12px' }}>
          <ReceiptAnimRow name="ASDA" initials="AS" bg="#534AB7" amount="£24.50" sub="26 Jun · 6 items" isMobile={isMobile} />

          {/* Tesco — share target */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 6,
            padding: isMobile ? '6px 5px' : '7px 6px',
            background: '#fff', borderRadius: isMobile ? 7 : 8,
            marginBottom: isMobile ? 3 : 4,
            transition: 'box-shadow 0.3s',
            boxShadow: rowRing ? '0 0 0 2px #2D5BFF' : 'none',
          }}>
            <div style={{
              width: isMobile ? 22 : 26, height: isMobile ? 22 : 26,
              borderRadius: 5, background: '#185FA5', color: '#fff',
              fontSize: isMobile ? 7 : 8, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Manrope, sans-serif', flexShrink: 0,
            }}>TE</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#14213D', fontFamily: 'Manrope, sans-serif' }}>Tesco</div>
              <div style={{ fontSize: isMobile ? 7 : 9, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>22 Jun · 3 items</div>
            </div>
            <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#14213D', fontFamily: 'Manrope, sans-serif', marginRight: 2 }}>£18.90</div>
            <div
              ref={shareBtnRef}
              style={{
                width: isMobile ? 20 : 24, height: isMobile ? 20 : 24,
                borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.3s',
                background: shareHighlighted ? '#2D5BFF' : '#F0F2F5',
              }}
            >
              <Users style={{
                width: isMobile ? 10 : 12, height: isMobile ? 10 : 12,
                color: shareHighlighted ? '#fff' : '#9CA3AF',
                transition: 'color 0.3s',
              }} />
            </div>
          </div>

          <ReceiptAnimRow name="Shell Petrol" initials="SH" bg="#BA7517" amount="£52.30" sub="20 Jun · 1 item" isMobile={isMobile} />
        </div>

        {/* Dim overlay */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: overlayDim ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0)',
          pointerEvents: 'none', transition: 'background 0.3s', zIndex: 2,
        }} />

        {/* Bottom sheet */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          background: '#fff', borderRadius: '14px 14px 0 0',
          transform: sheetOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.4s cubic-bezier(0.32,0.72,0,1)',
          padding: isMobile ? '8px 10px 12px' : '10px 14px 16px',
          zIndex: 3,
        }}>
          <div style={{ width: 24, height: 3, borderRadius: 2, background: '#ddd', margin: '0 auto 6px' }} />
          <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 600, color: '#14213D', fontFamily: 'Manrope, sans-serif' }}>Share with circles</div>
          <div style={{ fontSize: isMobile ? 8 : 9, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif', marginBottom: 6 }}>Select a group to share this receipt</div>

          {/* Spain Trip */}
          <div
            ref={groupRowRef}
            style={{
              display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 6,
              padding: '5px 0', borderTop: '0.5px solid #F3F4F6',
            }}
          >
            <div style={{
              fontSize: isMobile ? 13 : 15, width: isMobile ? 22 : 26, height: isMobile ? 22 : 26,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 999, background: '#E6F1FB', flexShrink: 0,
            }}>✈️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 500, color: '#14213D', fontFamily: 'Manrope, sans-serif' }}>Spain Trip</div>
              <div style={{ fontSize: isMobile ? 7 : 9, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>3 members</div>
            </div>
            <div style={{
              width: isMobile ? 14 : 16, height: isMobile ? 14 : 16,
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.3s', flexShrink: 0,
              border: checkChecked ? '1.5px solid #2D5BFF' : '1.5px solid #D1D5DB',
              background: checkChecked ? '#2D5BFF' : 'transparent',
            }}>
              {checkChecked && (
                <svg width={isMobile ? 8 : 10} height={isMobile ? 8 : 10} viewBox="0 0 10 10" fill="none">
                  <path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>

          <GroupAnimRow emoji="👨‍👩‍👧‍👦" name="Family group" meta="2 members" bg="#EAF3DE" isMobile={isMobile} />
          <GroupAnimRow emoji="🎂" name="Sarah's birthday" meta="4 members" bg="#FAEEDA" isMobile={isMobile} />

          <button
            ref={updateBtnRef}
            style={{
              width: '100%', border: 'none', marginTop: 6,
              fontSize: isMobile ? 10 : 11, borderRadius: 7,
              padding: isMobile ? '6px' : '7px',
              fontWeight: 600, fontFamily: 'Manrope, sans-serif',
              transition: 'all 0.3s', cursor: 'default',
              background: btnActive ? '#2D5BFF' : '#D1D5DB',
              color: '#fff',
              transform: step === 6 ? 'scale(0.96)' : 'scale(1)',
            }}
          >
            Update sharing
          </button>
        </div>

        {/* Success overlay */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.97)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          opacity: showSuccess ? 1 : 0, pointerEvents: 'none',
          transition: 'opacity 0.4s', zIndex: 4,
        }}>
          <div style={{
            width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
            borderRadius: '50%', background: '#1D9E75',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
          }}>
            <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 22 22" fill="none">
              <path d="M5 11L9.5 15.5L17 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div style={{ fontSize: isMobile ? 13 : 15, fontWeight: 600, color: '#14213D', fontFamily: 'Manrope, sans-serif' }}>Shared</div>
          <div style={{ fontSize: isMobile ? 9 : 11, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif', marginTop: 3 }}>
            Tesco is now shared with <span style={{ color: '#2D5BFF', fontWeight: 600 }}>Spain Trip</span>
          </div>
        </div>

        {/* Tap indicator */}
        <div style={{
          position: 'absolute', left: tapPos.x, top: tapPos.y,
          zIndex: 5, pointerEvents: 'none',
          opacity: showTap ? 1 : 0, transition: 'opacity 0.3s',
        }}>
          <div style={{
            width: isMobile ? 26 : 30, height: isMobile ? 26 : 30,
            borderRadius: '50%', background: 'rgba(45,91,255,0.15)',
            border: '2px solid rgba(45,91,255,0.4)',
            transform: 'translate(-50%, -50%)',
          }} />
        </div>
      </div>

      {/* Copy */}
      <div style={{
        fontWeight: 700, fontSize: 17, color: '#111827',
        fontFamily: 'Manrope, sans-serif', marginBottom: 4,
      }}>
        No receipts shared in this group yet
      </div>
      <div style={{
        color: '#9CA3AF', fontSize: 13,
        fontFamily: 'DM Sans, sans-serif', marginBottom: 16,
        maxWidth: 280, lineHeight: 1.5,
      }}>
        Receipts you scan and share with this group will appear here.
      </div>

      {/* Primary CTA */}
      <button
        onClick={onShareExisting}
        className="flex items-center gap-2"
        style={{
          background: GRADIENT_HEADER, color: '#fff', border: 'none',
          padding: '10px 24px', borderRadius: 999,
          fontSize: 14, fontWeight: 700,
          fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
        }}
      >
        <Users style={{ width: 16, height: 16 }} />
        Share an existing receipt
      </button>
    </div>
  );
};

export default function ReceiptsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const hasLoaded = useRef(false);

  // Group switcher state
  const [showGroupSwitcher, setShowGroupSwitcher] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupReceipts, setGroupReceipts] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const isGroupView = !!selectedGroup;

  // Share receipt sheet state
  const [shareReceiptTarget, setShareReceiptTarget] = useState(null); // receipt to share (null = closed)

  // Contextual share hint banner (set when coming from GroupEmptyState CTA)
  const [shareHintGroup, setShareHintGroup] = useState(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Read share hint from sessionStorage (written by GroupEmptyState CTA)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('mb_share_hint_group');
      if (saved) setShareHintGroup(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  // Auto-dismiss share hint after 60s
  useEffect(() => {
    if (!shareHintGroup) return;
    const timer = setTimeout(() => {
      setShareHintGroup(null);
      try { sessionStorage.removeItem('mb_share_hint_group'); } catch { /* ignore */ }
    }, 60000);
    return () => clearTimeout(timer);
  }, [shareHintGroup]);

  const fetchReceipts = useCallback(async () => {
    try {
      if (!hasLoaded.current) setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/receipts`, { withCredentials: true });
      if (response.data.success) setReceipts(response.data.receipts);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else setError('Failed to load receipts. Please try again.');
    } finally {
      setLoading(false);
      hasLoaded.current = true;
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchReceipts();
  }, [user, navigate, fetchReceipts]);

  // Fetch groups for switcher + restore persisted selection
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`, { withCredentials: true });
        const fetched = res.data.groups || [];
        setGroups(fetched);
        // Restore previously selected group from sessionStorage
        try {
          const saved = sessionStorage.getItem('mb_selected_group');
          if (saved) {
            const parsed = JSON.parse(saved);
            const match = fetched.find(g => g.id === parsed.id);
            if (match) setSelectedGroup(match);
          }
        } catch { /* ignore */ }
      } catch { /* silent */ }
    };
    fetchGroups();
  }, []);

  // Fetch group receipts when a group is selected
  useEffect(() => {
    if (!selectedGroup) { setGroupReceipts([]); setGroupMembers([]); return; }
    const fetchGroupData = async () => {
      setGroupLoading(true);
      try {
        const [itemsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/groups/${selectedGroup.id}/items?type=receipt`, { withCredentials: true }),
          axios.get(`${API}/groups/${selectedGroup.id}/summary`, { withCredentials: true }),
        ]);
        setGroupReceipts((itemsRes.data.items || []).map(i => ({
          ...i.item,
          _shared_by: i.shared_by_name,
          _share_id: i.share_id,
          _reimbursement_status: i.reimbursement_status,
          _paid_by_name: i.paid_by_name,
          _paid_by_label_name: i.paid_by_label_name,
          _paid_by_type: i.paid_by_type,
        })));
        setGroupMembers(summaryRes.data.members || []);
      } catch { }
      finally { setGroupLoading(false); }
    };
    fetchGroupData();
  }, [selectedGroup]);

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setShowGroupSwitcher(false);
    setSearchQuery('');
    setSelectedMonth(null);
    // Persist selection
    try {
      if (group) sessionStorage.setItem('mb_selected_group', JSON.stringify({ id: group.id }));
      else sessionStorage.removeItem('mb_selected_group');
    } catch { /* ignore */ }
  };

  // ── Auto-refresh while any receipt is still processing ─────────────────────
  useEffect(() => {
    const hasPending = receipts.some(
      (r) => r.scan_status === 'pending_retry' || r.scan_status === 'needs_review'
    );
    if (!hasPending) return;
    const id = setInterval(fetchReceipts, 10000);
    return () => clearInterval(id);
  }, [receipts, fetchReceipts]);

  // Auto-generated month filters — always current
  const monthFilters = useMemo(() => {
    const months = [{ label: 'All time', startDate: null, endDate: null }];
    const now = new Date();
    for (let i = 0; i < 4; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        label: date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        startDate: date,
        endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0),
      });
    }
    return months;
  }, []);

  // Parse "YYYY-MM-DD" as local midnight (not UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date(NaN);
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const sourceReceipts = isGroupView ? groupReceipts : receipts;

  const filteredReceipts = useMemo(() => {
    return sourceReceipts.filter((r) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !r.merchant?.toLowerCase().includes(q) &&
          !(r.category || '').toLowerCase().includes(q) &&
          !(r.notes || '').toLowerCase().includes(q)
        ) return false;
      }
      if (selectedMonth) {
        const d = parseLocalDate(r.transaction_date);
        if (d < selectedMonth.startDate || d > selectedMonth.endDate) return false;
      }
      return true;
    });
  }, [sourceReceipts, searchQuery, selectedMonth]);

  const groupedReceipts = useMemo(() => {
    const groups = {};
    filteredReceipts.forEach((r) => {
      const d = parseLocalDate(r.transaction_date);
      const key = isNaN(d)
        ? 'Unknown'
        : d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }).toUpperCase();
      if (!groups[key]) {
        groups[key] = {
          label: key,
          receipts: [],
          total: 0,
          // Store the first-of-month timestamp for sorting groups
          sortDate: isNaN(d) ? 0 : new Date(d.getFullYear(), d.getMonth(), 1).getTime(),
        };
      }
      groups[key].receipts.push(r);
      groups[key].total += Number(r.amount || 0);
    });

    // Sort groups newest-first, then sort receipts within each group newest-first
    return Object.values(groups)
      .sort((a, b) => b.sortDate - a.sortDate)
      .map((g) => ({
        ...g,
        receipts: g.receipts.sort((a, b) => {
          const da = parseLocalDate(a.transaction_date)?.getTime() || 0;
          const db = parseLocalDate(b.transaction_date)?.getTime() || 0;
          return db - da;
        }),
      }));
  }, [filteredReceipts]);

  const [couldntReadReceipt, setCouldntReadReceipt] = useState(null);

  const handleViewReceipt = useCallback((receipt) => {
    const action = getReceiptTapAction(receipt.scan_status);
    if (action === 'processing-toast') {
      toast.info("Still processing this one — check back in a few minutes.", {
        description: "We'll scan it in the background. Usually ready within 15 minutes.",
        action: {
          label: "Dismiss",
          onClick: () => { },
        },
      });
      return;
    }
    if (action === 'couldnt-read-modal') {
      setCouldntReadReceipt(receipt);
      return;
    }
    navigate(`/receipts/${receipt.id}`);
  }, [navigate]);

  const handleScanComplete = useCallback(() => {
    setShowScanModal(false);
    fetchReceipts();
    toast.success('Receipt added successfully');
  }, [fetchReceipts]);

  const dismissShareHint = useCallback(() => {
    setShareHintGroup(null);
    try { sessionStorage.removeItem('mb_share_hint_group'); } catch { /* ignore */ }
  }, []);

  const handleNavClick = (key) => {
    if (key === 'home') navigate('/dashboard');
    else if (key === 'receipts') navigate('/receipts');
    else if (key === 'cards') navigate('/cards');
    else if (key === 'insights') navigate('/insights');
    else if (key === 'profile') navigate('/profile');
    else toast.info(`${key.charAt(0).toUpperCase() + key.slice(1)} coming soon`);
  };

  if (!user) return null;

  // ── Shared Header ─────────────────────────────────────────────────────────
  const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';
  const avatarUrl = user?.avatar_url;

  const pageHeader = (
    <header
      style={{
        background: GRADIENT_HEADER,
        paddingTop: 32,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: (isGroupView && filteredReceipts.length === 0 && !searchQuery && !selectedMonth) ? 12 : 22,
      }}
    >
      {/* Title row — pill on right, matches Dashboard layout */}
      <div className="flex items-start justify-between mb-4">
        <div style={{ minWidth: 0, flex: 1, marginRight: 12 }}>
          <h1
            style={{
              color: '#FFFFFF',
              fontSize: 26,
              fontWeight: 800,
              fontFamily: 'Manrope, sans-serif',
              letterSpacing: -0.5,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isGroupView ? (selectedGroup?.name || 'My Receipts') : 'My Receipts'}
          </h1>
          {isGroupView ? (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center">
                {groupMembers.slice(0, 3).map((m, i) => {
                  const name = m.display_name || '?';
                  const initials = getMemberInitials(name);
                  const bg = getMemberAvatarColor(name);
                  return (
                    <div key={i} className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                      width: 28, height: 28, borderRadius: 999, backgroundColor: bg,
                      border: '2px solid rgba(45,91,255,0.8)', marginLeft: i > 0 ? -8 : 0,
                      zIndex: groupMembers.length - i, position: 'relative',
                      color: '#FFFFFF', fontSize: 10, fontWeight: 700, fontFamily: 'Manrope, sans-serif',
                    }}>
                      {initials}
                    </div>
                  );
                })}
              </div>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                {selectedGroup.member_count || groupMembers.length} member{(selectedGroup.member_count || groupMembers.length) !== 1 ? 's' : ''} sharing
              </span>
            </div>
          ) : (
            <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
              Never lose a receipt again
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <GroupSwitcherPill
            selectedGroup={selectedGroup}
            avatarUrl={avatarUrl}
            userName={userName}
            onClick={() => setShowGroupSwitcher(true)}
          />
          <button
            onClick={() => setShowScanModal(true)}
            className="flex items-center gap-1.5"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#FFFFFF',
              padding: '8px 14px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            <ScanLine className="w-4 h-4" />
            Scan
          </button>
        </div>
      </div>

      {/* White search bar */}
      <div className="relative mb-4">
        <Search
          className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4"
          style={{ color: '#94A3B8' }}
        />
        <input
          type="text"
          placeholder="Search receipts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
          style={{
            height: 44,
            paddingLeft: 38,
            paddingRight: searchQuery ? 36 : 14,
            borderRadius: 14,
            backgroundColor: '#FFFFFF',
            border: 'none',
            outline: 'none',
            fontSize: 16,
            fontFamily: 'DM Sans, sans-serif',
            color: '#0F172A',
            boxShadow: '0 6px 16px rgba(15,23,42,0.08)',
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full"
            style={{ backgroundColor: '#CBD5E1' }}
          >
            <X className="w-3 h-3" style={{ color: '#FFFFFF' }} />
          </button>
        )}
      </div>

      {/* Month filters — hidden when group empty state is showing */}
      {!(isGroupView && filteredReceipts.length === 0 && !searchQuery && !selectedMonth) && (
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none' }}
        >
          {monthFilters.map((month, idx) => (
            <MonthFilterPill
              key={idx}
              month={month}
              isActive={
                selectedMonth === null
                  ? month.label === 'All time'
                  : selectedMonth.label === month.label
              }
              onClick={() => setSelectedMonth(month.label === 'All time' ? null : month)}
            />
          ))}
        </div>
      )}
    </header>
  );

  // ── Shared Body ───────────────────────────────────────────────────────────
  const pageBody = (
    <div className="px-4 md:px-6 py-5 flex-1 overflow-y-auto">
      {(loading || (isGroupView && groupLoading)) ? (
        <div className="space-y-3 mt-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-16 bg-white rounded-2xl animate-pulse border border-gray-100"
            />
          ))}
        </div>
      ) : error ? (
        <div className="p-5 rounded-2xl border border-red-200 bg-red-50 flex gap-3 mt-2">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900 text-sm">{error}</div>
            <button
              onClick={fetchReceipts}
              className="mt-2 text-sm text-red-600 font-semibold underline"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <>
          {filteredReceipts.length === 0 ? (
            isGroupView && !searchQuery && !selectedMonth ? (
              <GroupEmptyState
                groupName={selectedGroup?.name}
                onShareExisting={() => {
                  // Write hint so Personal view shows the contextual banner
                  try {
                    if (selectedGroup) {
                      const hint = { id: selectedGroup.id, name: selectedGroup.name, emoji: selectedGroup.emoji || null };
                      sessionStorage.setItem('mb_share_hint_group', JSON.stringify(hint));
                      setShareHintGroup(hint);
                    }
                  } catch { /* ignore */ }
                  handleGroupSelect(null);
                }}
                isMobile={isMobile}
              />
            ) : (
              <EmptyState
                searchQuery={searchQuery}
                selectedMonth={selectedMonth}
                onScan={() => setShowScanModal(true)}
              />
            )
          ) : (
            groupedReceipts.map((group) => (
              <MonthSection
                key={group.label}
                label={group.label}
                receipts={group.receipts}
                onView={handleViewReceipt}
                onShare={!isGroupView ? (receipt) => setShareReceiptTarget(receipt) : undefined}
              />
            ))
          )}
        </>
      )}
    </div>
  );

  // ── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F7FA' }}>
        <div className="flex-1 flex flex-col pb-16">
          {pageHeader}
          {pageBody}
        </div>

        {/* Bottom nav */}
        <nav
          className="fixed bottom-0 left-0 right-0 flex justify-around items-center z-50"
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F3F4F6',
            paddingTop: 10,
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          }}
        >
          {NAV_ITEMS.map((item) => {
            const active = item.key === 'receipts';
            return (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.key)}
                className="flex flex-col items-center gap-1"
              >
                <item.icon
                  className="w-5 h-5"
                  style={{ color: active ? PRIMARY : '#9CA3AF' }}
                  fill={active ? PRIMARY : 'none'}
                />
                <span style={{
                  color: active ? PRIMARY : '#9CA3AF',
                  fontSize: 11,
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: active ? 600 : 500,
                }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {showScanModal && (
          <ScanReceiptModal
            isOpen={showScanModal}
            onClose={() => setShowScanModal(false)}
            onComplete={handleScanComplete}
            isMobile={true}
            onSaved={fetchReceipts}
          />
        )}

        <CouldntReadModal
          receipt={couldntReadReceipt}
          isOpen={!!couldntReadReceipt}
          onClose={() => setCouldntReadReceipt(null)}
          onChanged={() => { setCouldntReadReceipt(null); fetchReceipts(); }}
        />
        <GroupSwitcherSheet
          isOpen={showGroupSwitcher}
          onClose={() => setShowGroupSwitcher(false)}
          groups={groups}
          selectedGroupId={selectedGroup?.id}
          onSelect={handleGroupSelect}
          onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
          onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
          userName={userName}
          avatarUrl={avatarUrl}
        />
        <ShareReceiptSheet
          isOpen={!!shareReceiptTarget}
          receipt={shareReceiptTarget}
          groups={groups}
          onClose={() => setShareReceiptTarget(null)}
          onShared={() => { dismissShareHint(); setShareReceiptTarget(null); }}
        />
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Sidebar */}
      <aside
        className="flex flex-col flex-shrink-0"
        style={{ width: 220, backgroundColor: '#FFFFFF', borderRight: '1px solid #F3F4F6' }}
      >
        <div
          className="flex items-center gap-2 px-5 py-5"
          style={{ borderBottom: '1px solid #F3F4F6' }}
        >
          <BuddyLogo size={28} />
          <span style={{
            color: '#111827', fontSize: 18, fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
          }}>
            My Buddy
          </span>
        </div>
        <nav className="flex-1 px-3 py-3">
          {NAV_ITEMS.map((item) => {
            const active = item.key === 'receipts';
            return (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.key)}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
                style={{
                  backgroundColor: active ? '#E0E7FF' : 'transparent',
                  color: active ? PRIMARY : '#6B7280',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center overflow-hidden rounded-full flex-shrink-0"
              style={{
                width: 40, height: 40,
                backgroundColor: user?.avatar_url ? 'transparent' : PRIMARY,
                color: '#fff',
                fontWeight: 700, fontFamily: 'Manrope, sans-serif',
              }}
            >
              {user?.avatar_url
                ? <img src={user.avatar_url} alt={user?.name || 'User'} className="w-full h-full object-cover" />
                : (user?.first_name || user?.name || user?.email || 'U').charAt(0).toUpperCase()
              }
            </div>
            <div className="min-w-0">
              <p className="truncate profile-no-underline" style={{
                color: '#111827', fontSize: 14,
                fontWeight: 600, fontFamily: 'Manrope, sans-serif',
              }}>
                {(user?.first_name && user?.last_name)
                  ? `${user.first_name} ${user.last_name}`
                  : user?.name || 'User'}
              </p>
              <p className="truncate profile-no-underline" style={{
                color: '#6B7280', fontSize: 12,
                fontFamily: 'DM Sans, sans-serif',
              }}>
                {user?.email || ''}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto flex flex-col">
        {pageHeader}
        {pageBody}
      </main>

      {showScanModal && (
        <ScanReceiptModal
          isOpen={showScanModal}
          onClose={() => setShowScanModal(false)}
          onComplete={handleScanComplete}
          isMobile={false}
          onSaved={fetchReceipts}
        />
      )}

      <CouldntReadModal
        receipt={couldntReadReceipt}
        isOpen={!!couldntReadReceipt}
        onClose={() => setCouldntReadReceipt(null)}
        onChanged={() => { setCouldntReadReceipt(null); fetchReceipts(); }}
      />
      <GroupSwitcherSheet
        isOpen={showGroupSwitcher}
        onClose={() => setShowGroupSwitcher(false)}
        groups={groups}
        selectedGroupId={selectedGroup?.id}
        onSelect={handleGroupSelect}
        onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
        onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
        userName={userName}
        avatarUrl={avatarUrl}
      />
      <ShareReceiptSheet
        isOpen={!!shareReceiptTarget}
        receipt={shareReceiptTarget}
        groups={groups}
        onClose={() => setShareReceiptTarget(null)}
        onShared={() => { dismissShareHint(); setShareReceiptTarget(null); }}
      />
    </div>
  );
}